import { fetchReticulumAuthenticated } from "./phoenix-utils";
import { proxiedUrlFor } from "./media-url-utils";
import avatarUnavailableImage from "../assets/images/avatar_unavailable.png";
import configs from "./configs";
import { getBundledDefaultAvatarUrl } from "./default-avatar-url";
import { fetchRandomPlatformPickAvatarId } from "./identity";
import { AVATAR_TYPES, getAvatarType } from "./avatar-type-utils";

const AVATARS_API = "/api/v1/avatars";

export { AVATAR_TYPES, getAvatarType };

async function fetchSkinnableAvatar(avatarId) {
  // Assuming global lookup by SID exists at /avatars/:sid
  let resp;
  try {
    resp = await fetch(`${configs.PERSISTENCE_API_URL}/avatars/${avatarId}`);
  } catch (err) {
    console.warn(`[AvatarUtils] Failed to fetch avatar ${avatarId} (network error):`, err);
    return null;
  }

  if (!resp.ok) {
    console.error(`[AvatarUtils] Failed to fetch avatar ${avatarId}: ${resp.status} ${resp.statusText}`);
    return null;
  }

  const data = await resp.json();
  const avatar = data.data || data;

  if (avatar) {
    const url = avatar.url || avatar.asset?.url;
    avatar.gltf_url = proxiedUrlFor(url);
    avatar.avatar_id = avatarId;
    avatar.files = {
      thumbnail: avatar.thumbnail || avatar.asset?.thumbnail
    };
  }
  return avatar;
}

async function fetchPersistenceAvatar(avatarId) {
  const { getAuthHeaders } = await import("./getToken");
  let authHeaders = {};
  try {
    authHeaders = await getAuthHeaders(avatarId, "GET");
  } catch (e) {
    console.warn("fetchAvatar: getAuthHeaders failed or not initialized, trying without auth headers.", e);
  }

  let resp;
  try {
    resp = await fetch(avatarId, { headers: authHeaders });
  } catch (err) {
    console.warn(`[AvatarUtils] Failed to fetch persistence avatar ${avatarId} (network error):`, err);
    return null;
  }

  if (!resp.ok) {
    console.warn(`[AvatarUtils] Failed to fetch persistence avatar ${avatarId}: ${resp.status} ${resp.statusText}`);
    return null;
  }

  let data;
  try {
    data = await resp.json();
  } catch (err) {
    console.warn(`[AvatarUtils] Failed to parse persistence avatar ${avatarId}:`, err);
    return null;
  }

  const asset = data.data || data;
  if (!asset?.url) {
    console.warn(`[AvatarUtils] Persistence avatar ${avatarId} did not return a usable asset URL.`);
    return null;
  }

  return {
    ...asset,
    avatar_id: avatarId,
    gltf_url: proxiedUrlFor(asset.url),
    files: {
      thumbnail: asset.thumbnail
    }
  };
}

const inFlightRequests = new Map();

async function fetchAvatarInternal(avatarId) {
  switch (getAvatarType(avatarId)) {
    case AVATAR_TYPES.SKINNABLE:
      return fetchSkinnableAvatar(avatarId);
    case AVATAR_TYPES.PERSISTENCE:
      return fetchPersistenceAvatar(avatarId);
    case AVATAR_TYPES.URL:
      return {
        avatar_id: avatarId,
        gltf_url: proxiedUrlFor(avatarId),
        files: {
          thumbnail: avatarUnavailableImage
        }
      };
  }
}

export async function fetchAvatar(avatarId) {
  if (!avatarId) return null;

  if (inFlightRequests.has(avatarId)) {
    return inFlightRequests.get(avatarId);
  }

  const promise = fetchAvatarInternal(avatarId).finally(() => {
    inFlightRequests.delete(avatarId);
  });

  inFlightRequests.set(avatarId, promise);
  return promise;
}

async function fetchAvatarGltfUrl(avatarId) {
  return fetchAvatar(avatarId).then(avatar => avatar && avatar.gltf_url);
}

export function getAvatarSrc(avatarId) {
  switch (getAvatarType(avatarId)) {
    case AVATAR_TYPES.SKINNABLE:
      return fetchAvatarGltfUrl(avatarId);
    case AVATAR_TYPES.PERSISTENCE:
      return fetchAvatar(avatarId).then(avatar => avatar && avatar.gltf_url);
    case AVATAR_TYPES.URL:
      return proxiedUrlFor(avatarId);
    default:
      return avatarId;
  }
}

function setStoreAvatarId(store, avatarId) {
  if (!store?.update) return;

  store.update({
    profile: {
      ...(store.state?.profile || {}),
      avatarId
    }
  });
}

export async function resolveAvatarSrcWithFallback(avatarId, { store } = {}) {
  let resolvedAvatarId = avatarId;
  let avatarSrc = await getAvatarSrc(resolvedAvatarId);

  if (avatarSrc) {
    return { avatarId: resolvedAvatarId, avatarSrc };
  }

  if (store?.resetToRandomDefaultAvatar) {
    await store.resetToRandomDefaultAvatar();
    resolvedAvatarId = store.state?.profile?.avatarId;
  } else {
    resolvedAvatarId = await fetchRandomPlatformPickAvatarId();
  }

  avatarSrc = await getAvatarSrc(resolvedAvatarId);
  if (avatarSrc) {
    return { avatarId: resolvedAvatarId, avatarSrc };
  }

  const bundledAvatarUrl = getBundledDefaultAvatarUrl(configs.BASE_ASSETS_PATH);
  if (resolvedAvatarId !== bundledAvatarUrl) {
    setStoreAvatarId(store, bundledAvatarUrl);
  }

  return {
    avatarId: bundledAvatarUrl,
    avatarSrc: bundledAvatarUrl
  };
}

export async function getAvatarThumbnailUrl(avatarId) {
  switch (getAvatarType(avatarId)) {
    case AVATAR_TYPES.SKINNABLE:
    case AVATAR_TYPES.PERSISTENCE:
      return fetchAvatar(avatarId).then(avatar => avatar?.files?.thumbnail || avatarUnavailableImage);
    default:
      return avatarUnavailableImage;
  }
}

// Currently the way we do material overrides is with a special named material.
// We want to migrate eventually to having a GLTF extension that specifies what
// materials can be overridden, but in the meantime we want to be able to support
// arbitrary models with some sort of functionality. This provides a fallback
export const MAT_NAME = "Bot_PBS";
export function ensureAvatarMaterial(gltf) {
  if (gltf.materials.find(m => m.name === MAT_NAME)) return gltf;

  function materialForMesh(mesh) {
    if (!mesh.primitives) return;
    const primitive = mesh.primitives.find(p => p.material !== undefined);
    return primitive && gltf.materials[primitive.material];
  }

  let nodes = gltf.scenes[gltf.scene || 0].nodes.slice(0);
  while (nodes.length) {
    const node = gltf.nodes[nodes.shift()];
    const material = node.mesh !== undefined && materialForMesh(gltf.meshes[node.mesh]);
    if (material) {
      material.name = MAT_NAME;
      break;
    }
    if (node.children) nodes = nodes.concat(node.children);
  }

  return gltf;
}

export async function remixAvatar(parentId, name) {
  const avatar = {
    parent_avatar_listing_id: parentId,
    name: name,
    files: {}
  };

  return fetchReticulumAuthenticated(AVATARS_API, "POST", { avatar });
}

export function isFullBodyMale(model) {
  const leftShoulder = model.getObjectByName("LeftShoulder");
  const rightShoulder = model.getObjectByName("RightShoulder");

  if (leftShoulder && rightShoulder) {
    const leftPosition = Number(leftShoulder.position.x.toFixed(2));
    const rightPosition = Number(rightShoulder.position.x.toFixed(2));
    const targetNumber = 0.05;

    if (Math.abs(leftPosition) == targetNumber && Math.abs(rightPosition) == targetNumber) {
      return true;
    }
  }

  return false;
}

export function getAnimationProfile(model) {
  const profile = model?.userData?.animationProfile;

  if (profile === "male" || profile === "female") {
    return profile;
  }

  return isFullBodyMale(model) ? "male" : "female";
}

export function isMaleAnimationProfile(model) {
  const profile = model?.userData?.animationProfile;

  if (profile === "male" || profile === "female") {
    return profile === "male";
  }

  return Boolean(model?.userData?.isMaleAnim);
}

export function setAnimationProfileMetadata(model, isFullBody, profile) {
  const isMale = profile === "male";

  model.userData.isFullBody = isFullBody;
  model.userData.isMaleAnim = isMale;
  model.userData.isFemaleAnim = !isMale;

  if (isFullBody) {
    model.userData.animationProfile = isMale ? "male" : "female";
  } else {
    delete model.userData.animationProfile;
  }
}
